import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { toast } from "react-toastify";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Input,
  Button,
  Chip,
  Tabs,
  Tab,
  User,
  Pagination,
  useDisclosure,
} from "@nextui-org/react";
import { IoSearchOutline } from "react-icons/io5";
import axios from "axios";
import AccessModal from "./AccessModal";
import { useDispatch } from "react-redux";
import { refreshIfNeeded } from "../../store/actions/refreshIfNeeded";

/* ───────── Column defs ───────── */
export const columns = [
  { name: "ID", uid: "id", sortable: true },
  { name: "NAME", uid: "name" },
  { name: "ROLE", uid: "role" },
  { name: "EMAIL", uid: "email" },
  { name: "PHONE", uid: "phone" },
  { name: "CAPRICORN", uid: "capricorn" },
  { name: "COMPANY", uid: "company" },
  { name: "STATUS", uid: "status" },
  { name: "ACTIONS", uid: "actions" },
];

const statusColorMap = {
  PENDING: { color: "warning", variant: "flat", radius: "sm" },
  APPROVED: { color: "success", variant: "flat", radius: "sm" },
  REJECTED: { color: "danger", variant: "flat", radius: "sm" },
};

const INITIAL_VISIBLE_COLUMNS = new Set([
  "name",
  "role",
  "phone",
  "capricorn",
  "company",
  "actions",
]);

export default function RequestApproval() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const dispatch = useDispatch();

  const [userLists, setUserLists] = useState([]);
  const [statusFilter, setStatusFilter] = useState("PENDING");
  const [sortDescriptor, setSortDescriptor] = useState({ column: null, direction: null });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(6);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalAction, setModalAction] = useState("");
  const [busy, setBusy] = useState(false);

  // Server-side search
  const [search, setSearch] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const debounceRef = useRef(null);
  const [batchPage, setBatchPage] = useState(1);
  const [batchSize] = useState(7);
  const [allDataLoaded, setAllDataLoaded] = useState(false);
//  const [totalPages, StotalPages] = useState(0);
  const ensureAuth = useCallback(async () => {
    await dispatch(refreshIfNeeded());
    const fresh = localStorage.getItem("accessToken");
    return { headers: { Authorization: `Bearer ${fresh}` } };
  }, [dispatch]);

  // Fetch users from server
  const getAllUsers = useCallback(
    async (pageToFetch = 1, { replace = false, searchParam = search } = {}) => {
      try {
        const auth = await ensureAuth();
        const qp = new URLSearchParams();
        qp.append("page", pageToFetch);
        qp.append("pageSize", batchSize);
        if (statusFilter) qp.append("status", statusFilter);
        if (searchParam?.trim()) qp.append("search", searchParam.trim());
        if (sortDescriptor.column) {
          qp.append("sortBy", sortDescriptor.column);
          qp.append("sortOrder", sortDescriptor.direction === "ascending" ? "asc" : "desc");
        }

        const { data } = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/admin/supplierrequestlist/?${qp}`,
          auth
        );

        const fetchedRows = data?.details?.requests || [];
        const total = data?.details?.pagination?.totalRecords ?? fetchedRows.length;
        setTotalRecords(total);

        if (replace || pageToFetch === 1) {
          setUserLists(fetchedRows);
        } else {
          setUserLists((prev) => {
            const prevIds = new Set(prev.map((r) => r.id));
            return [...prev, ...fetchedRows.filter((r) => !prevIds.has(r.id))];
          });
        }

        setBatchPage(pageToFetch);
        setAllDataLoaded(fetchedRows.length < batchSize);
    } catch (err) {
      console.error(err);
      const code = err?.response?.status;
      if (code === 401) {
        toast.error("Session expired. Please log in again.");
        return;
      }
      toast.error(err?.response?.data?.message ?? "Couldn’t load users");
    }
    },
    [ensureAuth, statusFilter, sortDescriptor, search, batchSize]
  );

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      getAllUsers(1, { replace: true }).catch(console.error);
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [search, getAllUsers]);

  // Initial load or status/sort change
  useEffect(() => {
    getAllUsers(1, { replace: true });
  }, [statusFilter, sortDescriptor]);

  const ensurePageData = useCallback(
    async (uiPage) => {
      const requiredEndIndex = uiPage * rowsPerPage;
      if (requiredEndIndex <= userLists.length) return;
      if (allDataLoaded) return;

      const requiredBatch = Math.ceil(requiredEndIndex / batchSize);
      for (let b = batchPage + 1; b <= requiredBatch; b++) {
        await getAllUsers(b, { replace: false });
        if (allDataLoaded) break;
      }
    },
    [userLists.length, allDataLoaded, rowsPerPage, batchPage, batchSize, getAllUsers]
  );

  // Only filter by status (server already handled search)
  const filteredItems = useMemo(() => {
    return userLists.filter((u) => u.approval_status === statusFilter);
  }, [userLists, statusFilter]);

 const getSortValue = (item, column) => {
  switch (column) {
    case "status":
      // Use approval_status directly
      return (item.approval_status || "").toLowerCase();

    case "name":
      // first_name + last_name directly on item
      return `${item.first_name || ""} ${item.last_name || ""}`.toLowerCase();

    
    case "capricorn":
      // Not in your object, maybe you want company_name
      return item.company_name || "";

    case "company":
      // Use createdAt if exists, else 0
      return new Date(item.createdAt || 0).getTime();

    case "delete":
      return 0;

    default:
      return item[column] || "";
  }
};

   
   
   
   
    // 1. Sorting logic
   const sortedItems = useMemo(() => {
     if (!sortDescriptor.column) return filteredItems;
   
     return [...filteredItems].sort((a, b) => {
       let v1 = getSortValue(a, sortDescriptor.column);
       let v2 = getSortValue(b, sortDescriptor.column);
   
   
       // Detect date
       const isDate1 = !isNaN(Date.parse(v1));
       const isDate2 = !isNaN(Date.parse(v2));
   
       if (isDate1 && isDate2) {
         v1 = new Date(v1).getTime();
         v2 = new Date(v2).getTime();
       }
   
       // Detect numbers
       const isNum1 = !isNaN(v1) && v1 !== "";
       const isNum2 = !isNaN(v2) && v2 !== "";
       if (isNum1 && isNum2) return sortDescriptor.direction === "ascending" ? v1 - v2 : v2 - v1;

      return sortDescriptor.direction === "ascending" ? v1.toString().localeCompare(v2.toString()) : v2.toString().localeCompare(v1.toString());
     });
   }, [filteredItems, sortDescriptor]);

  const paginatedItems = useMemo(
    () => sortedItems.slice((page - 1) * rowsPerPage, page * rowsPerPage),
    [sortedItems, page, rowsPerPage]
  );


const totalPages = Math.max(1, Math.ceil(sortedItems.length / rowsPerPage));
  /* Page reset if current > total after filters change */
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);
 
  /* ───── Cell renderer ───── */
  const renderCell = useCallback((user, columnKey) => {
    switch (columnKey) {
      case "name":
        return (
          <User
            avatarProps={{ radius: "lg", src: "" }}
            name={`${user.first_name} ${user.last_name}`}
            description={user.email}
          />
        );
      case "role":
        return (
          <div className="flex flex-col">
            <p className="text-bold text-small capitalize">SUPPLIER</p>
            <p className="text-bold text-tiny capitalize text-default-400">
              {user.team}
            </p>
          </div>
        );
      case "phone":
        return <p className="text-bold text-small">{user.phone}</p>;
      case "capricorn":
        return (
          <p className="text-bold text-small">
            {user.is_capricorn_supplier ? "Yes" : "No"}
          </p>
        );
      case "company":
        return <p className="text-bold text-small">{user.company_name}</p>;
      case "actions":
        if (user.approval_status !== "PENDING") {
          const { color, variant, radius } =
            statusColorMap[user.approval_status];
          return (
            <Chip color={color} variant={variant} radius={radius} size="sm">
              {user.approval_status}
            </Chip>
          );
        }
        return (
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button
              radius="sm"
              variant="flat"
              color="success"
              className="flex-1 sm:flex-none px-4 py-2 tracking-wide"
              onPress={() => {
                setSelectedUser(user);
                setModalAction("Approve");
                onOpen();
              }}
            >
              APPROVE
            </Button>
            <Button
              radius="sm"
              variant="flat"
              color="danger"
              className="flex-1 sm:flex-none px-4 py-2 tracking-wide"
              onPress={() => {
                setSelectedUser(user);
                setModalAction("Reject");
                onOpen();
              }}
            >
              REJECT
            </Button>
          </div>
        );
      default:
        return user[columnKey];
    }
  }, []);

  /* ───── Status update API ───── */
  const handleStatusChange = async () => {
    if (!selectedUser) return;
    try {
      setBusy(true);
      const approval_status =
        modalAction === "Approve" ? "APPROVED" : "REJECTED";
      const auth = await ensureAuth();
      await axios.put(
        `${process.env.REACT_APP_SERVER_URL}/admin/updatesupplierapproval/${selectedUser.id}`,
        { approval_status },
        {
          ...auth,
          headers: { ...auth.headers, "Content-Type": "application/json" },
        }
      );
      toast.success(
        `User ${modalAction === "Approve" ? "approved" : "rejected"}`
      );
      onClose();
      getAllUsers(1, { replace: true });
    } catch (err) {
      console.error(err);
      const code = err?.response?.status;
      if (code === 401) {
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error(err?.response?.data?.message ?? "Update failed");
      }
    } finally {
      setBusy(false);
    }
  };
  /* ───── Top‑bar (Tabs + Search + Rows selector) ───── */
  const topContent = useMemo(
    () => (
      <div className="flex flex-col gap-4">
        {/* Tabs */}
        <Tabs
          selectedKey={statusFilter}
          onSelectionChange={(key) => {
            setStatusFilter(key);
            setPage(1);
          }}
          aria-label="Status Filter Tabs"
          className="w-fit"
        >
          <Tab key="PENDING" title="Pending" />
          <Tab key="APPROVED" title="Approved" />
        </Tabs>
        {/* Search */}
        <div className="flex justify-between gap-3 items-end">
          <Input
            isClearable
            className="w-full sm:max-w-[44%]"
            placeholder="Search by name..."
            startContent={<IoSearchOutline />}
            value={search}
          onClear={() => { setSearch(""); setPage(1); }}
          onValueChange={(v) => 
            setSearch(v || "")}
          />
        </div>

        {/* Meta + Rows selector */}
        <div className="flex justify-between items-center">
          <span className="text-default-400 text-small">
            Total {userLists.length} {statusFilter.toLowerCase()} users
          </span>

          <label className="flex items-center text-default-400 text-small">
            Rows per page:
            <select
              value={rowsPerPage}
              className="bg-transparent outline-none ml-1"
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(1);
              }}
            >
              {[6, 10, 15].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
  ), [statusFilter, search, rowsPerPage, userLists.length]);

  /* ───── Bottom pagination ───── */
  const bottomContent = useMemo(
    () => (
      <div className="py-2 px-2 flex justify-center items-center">
        <Pagination
          isCompact
          showControls
          showShadow
          color="primary"
          page={page}
          total={totalPages}
        onChange={async (newPage) => { await ensurePageData(newPage); setPage(newPage); }}
      />
    </div>
  ), [page, totalPages, ensurePageData]);


  /* ───── Render ───── */
  return (
    <div className="mb-14">
      <Table
        isHeaderSticky
        topContent={topContent}
        topContentPlacement="outside"
        bottomContent={bottomContent}
        bottomContentPlacement="outside"
        sortDescriptor={sortDescriptor}
        onSortChange={setSortDescriptor}

      >
        <TableHeader
          columns={[...columns].filter((c) =>
            INITIAL_VISIBLE_COLUMNS.has(c.uid)
          )}
        >
          {(column) => (
            <TableColumn
              key={column.uid}
              align={column.uid === "actions" ? "center" : "start"}
             allowsSorting={
        column.uid === "capricorn" || 
        column.uid === "name" || 
        column.uid === "company".includes(column.uid)}>
              {column.name}
            </TableColumn>
          )}
        </TableHeader>

        <TableBody emptyContent={"No users found"} items={paginatedItems}>
          {(item) => (
            <TableRow key={item.id}>
              {(columnKey) => (
                <TableCell>{renderCell(item, columnKey)}</TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Modal */}
      {isOpen && (
        <AccessModal
          isOpen={isOpen}
          onClose={onClose}
          text={modalAction}
          handleSubmit={busy ? () => {} : handleStatusChange}
        />
      )}
    </div>
  );
}
